import { useCallback, useEffect, useRef, useState } from "react";
import { useI18n } from "@nelurio/i18n";
import { api, isDemoMode } from "../api/client";
import { fetchRoutines, routinesForDate, type Routine } from "../lib/routines";
import { isReminderMinute } from "../lib/reminders";

interface ScheduledLike { id: string; title: string; dueTime?: string | null; status?: string }

const ASK_KEY = "nelurio.push.ask.v1";
const SENT_KEY = "nelurio.localnoti.sent.v1";

function readSent(): Set<string> {
  try {
    const value = JSON.parse(window.localStorage.getItem(SENT_KEY) ?? "[]");
    const today = new Date().toDateString();
    const parsed = Array.isArray(value) && value[0] === today ? (value[1] as string[]) : [];
    return new Set(parsed);
  } catch {
    return new Set();
  }
}

function markSent(id: string) {
  const sent = readSent();
  sent.add(id);
  try { window.localStorage.setItem(SENT_KEY, JSON.stringify([new Date().toDateString(), [...sent]])); } catch { /* ignore */ }
}

/**
 * Device notifications for time-blocked tasks, routines and reviews.
 * Asks permission once (after login, never nagging), then fires a local
 * notification at the scheduled minute while the app is open (PWA-ready).
 */
export function useLocalNotifications(enabled: boolean) {
  const { t } = useI18n();
  const [permission, setPermission] = useState<NotificationPermission | "unsupported">(
    typeof Notification === "undefined" ? "unsupported" : Notification.permission
  );
  const fired = useRef<Set<string>>(readSent());

  const request = useCallback(async () => {
    if (typeof Notification === "undefined") return "unsupported" as const;
    const result = await Notification.requestPermission();
    setPermission(result);
    if (result === "granted") {
      try { window.localStorage.setItem(ASK_KEY, new Date().toISOString()); } catch { /* ignore */ }
    }
    return result;
  }, []);

  const notify = useCallback((title: string, body: string) => {
    if (typeof Notification === "undefined" || Notification.permission !== "granted") return;
    try { new Notification(title, { body, icon: "/nelurio-icon-192.png", tag: title }); } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (!enabled || permission !== "granted" || isDemoMode()) return;

    const tick = async () => {
      const now = new Date();
      // Zero-padded HH:MM to match server dueTime and <input type="time">.
      const minuteKey = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
      const dayKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
      // Routines + their completion state now come from the server.
      let routines: Routine[] = [];
      let doneRoutines: string[] = [];
      try {
        const data = await fetchRoutines(dayKey);
        routines = data.routines;
        doneRoutines = data.done;
      } catch { /* silent */ }

      // 1) routines scheduled for this minute
      for (const routine of routinesForDate(routines, now)) {
        const id = `routine:${dayKey}:${routine.id}`;
        if (routine.time && routine.time === minuteKey && !doneRoutines.includes(routine.id) && !fired.current.has(id)) {
          notify(t("notif.routineTitle"), `${routine.title} — ${t("routine.title")}`);
          markSent(id);
          fired.current.add(id);
        }
      }

      // 2) time-blocked tasks due this minute
      try {
        const tasks = await api<ScheduledLike[]>(`/tasks?date=${dayKey}`);
        for (const task of Array.isArray(tasks) ? tasks : []) {
          if (task.status === "completed" || !task.dueTime) continue;
          const id = `task:${dayKey}:${task.id}`;
          if (task.dueTime === minuteKey && !fired.current.has(id)) {
            notify(t("notif.taskTitle"), task.title);
            markSent(id);
            fired.current.add(id);
          }
          // 30-minute lead reminder (owner law) — while the app is open; the
          // server push covers the closed-app case.
          const leadId = `task+30:${dayKey}:${task.id}`;
          if (isReminderMinute(task.dueTime, now) && !fired.current.has(leadId)) {
            notify(t("notif.taskReminderTitle", { time: task.dueTime }), task.title);
            markSent(leadId);
            fired.current.add(leadId);
          }
        }
      } catch { /* silent */ }

      // 3) daily reminder for pending reviews at 09:00
      if (minuteKey === "09:00" && !fired.current.has(`reviews:${dayKey}`)) {
        try {
          const data = await api<{ reviews: Array<{ id: string }> }>(`/dashboard?date=${dayKey}`);
          const count = Array.isArray(data.reviews) ? data.reviews.length : 0;
          if (count > 0) {
            notify(t("notif.reviewsDueShort", { count }), t("notif.reviewsDueBodyShort"));
            markSent(`reviews:${dayKey}`);
            fired.current.add(`reviews:${dayKey}`);
          }
        } catch { /* silent */ }
      }
    };

    void tick();
    const timer = window.setInterval(tick, 30_000);
    return () => window.clearInterval(timer);
  }, [enabled, permission, notify, t]);

  return { permission, request };
}

/** Non-intrusive one-time permission ask after login (GDPR-friendly: opt-in). */
export function useNotificationPermissionPrompt(active: boolean) {
  const { request } = useLocalNotifications(active);
  const [asked, setAsked] = useState(() => {
    try { return Boolean(window.localStorage.getItem(ASK_KEY)); } catch { return true; }
  });
  useEffect(() => {
    if (!active || asked) return;
    const timer = window.setTimeout(async () => {
      if (typeof Notification !== "undefined" && Notification.permission === "default") {
        await request();
      }
      if (typeof Notification !== "undefined" && Notification.permission === "granted") {
        const { enableWebPush } = await import("./webPush");
        await enableWebPush().catch(() => undefined);
      }
      try { window.localStorage.setItem(ASK_KEY, new Date().toISOString()); } catch { /* ignore */ }
      setAsked(true);
    }, 4000);
    return () => window.clearTimeout(timer);
  }, [active, asked, request]);
}
